import { Toast } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AssistantModeEnum,
  MessageFromEnum,
  MessageStatusEnum,
  StreamState,
} from "types";
import { ThreadMessage } from "..";
import {
  createAssistantMessage,
  createUserMessage,
} from "../components/utils/chat.utils";
import { MESSAGES_PAGE_SIZE, useThreadChat } from "./chat-thread.hook";
import { useStreamPolling } from "./stream-polling.hook";

const SCROLL_THRESHOLD = 100;

interface UseThreadOptions {
  threadId: string | null;
  onThreadIdChange?: (threadId: string | null) => void;
}

export const useThread = ({ threadId, onThreadIdChange }: UseThreadOptions) => {
  const { t } = useI18n();
  const {
    sendMessage,
    getThread,
    fetchThreadSilent,
    loadMoreMessages,
    cancelMessage,
    getStreamState,
    isLoadingThread,
    threadError,
  } = useThreadChat();

  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [selectedMode, setSelectedMode] = useState<AssistantModeEnum>(
    AssistantModeEnum.AuremAgent,
  );
  const [isThinking, setIsThinking] = useState(false);
  const [streamingState, setStreamingState] = useState<StreamState | null>(
    null,
  );
  const [processingMessageId, setProcessingMessageId] = useState<string | null>(
    null,
  );
  const [offset, setOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollHeightBeforePrepend = useRef<number | null>(null);
  const pendingScrollToBottom = useRef(false);

  const scrollToBottom = useCallback(() => {
    pendingScrollToBottom.current = true;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (scrollHeightBeforePrepend.current !== null) {
      const scrollHeightIncrease =
        container.scrollHeight - scrollHeightBeforePrepend.current;
      container.scrollTop += scrollHeightIncrease;
      scrollHeightBeforePrepend.current = null;
      return;
    }

    if (pendingScrollToBottom.current) {
      pendingScrollToBottom.current = false;
      container.scrollTop = container.scrollHeight;
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = container;
    const nearBottom =
      scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
    if (nearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Load thread when threadId changes
  useEffect(() => {
    // Always reset state when threadId changes (including switching threads)
    setMessages([]);
    setOffset(0);
    setHasMoreMessages(false);
    setIsThinking(false);
    setStreamingState(null);
    setProcessingMessageId(null);

    if (!threadId) return;

    getThread(threadId)
      .then((thread) => {
        if (thread) {
          const reversed = [...thread.messagesList].reverse();
          scrollToBottom();
          setMessages(reversed);
          setOffset(MESSAGES_PAGE_SIZE);
          setHasMoreMessages(thread.messagesList.length === MESSAGES_PAGE_SIZE);
          const lastMessage = reversed[reversed.length - 1];
          const hasProcessing = thread.messagesList.some(
            (message) => message.status === MessageStatusEnum.Processing,
          );
          setIsThinking(hasProcessing);
          if (hasProcessing && lastMessage) {
            setProcessingMessageId(lastMessage.id);
          }
        }
      })
      .catch(() => {
        if (onThreadIdChange) {
          onThreadIdChange(null);
        }
        setMessages([]);
        setIsThinking(false);
        setStreamingState(null);
      });
  }, [threadId, getThread, onThreadIdChange]);

  const clearStreamingState = useCallback(() => {
    setIsThinking(false);
    setStreamingState(null);
  }, []);

  const clearMessageError = useCallback(
    (userMessageId: string, assistantMessageId: string) => {
      setProcessingMessageId(null);
      setMessages((prev) =>
        prev.filter(
          (message) =>
            message.id !== userMessageId && message.id !== assistantMessageId,
        ),
      );
      setIsThinking(false);
    },
    [],
  );

  const syncThreadMessages = useCallback(
    async (threadId: string) => {
      try {
        const thread = await fetchThreadSilent(threadId);
        if (thread) {
          const reversed = [...thread.messagesList].reverse();
          scrollToBottom();
          setMessages(reversed);
          const hasStuckProcessing = thread.messagesList.some(
            (msg) => msg.status === MessageStatusEnum.Processing,
          );
          return !hasStuckProcessing;
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    [fetchThreadSilent, scrollToBottom],
  );

  const handleLoadMore = useCallback(async () => {
    if (!threadId || isLoadingMore || !hasMoreMessages) return;
    setIsLoadingMore(true);
    const container = containerRef.current;
    if (container) {
      scrollHeightBeforePrepend.current = container.scrollHeight;
    }
    try {
      const olderMessages = await loadMoreMessages(threadId, offset);
      if (olderMessages.length === 0) {
        scrollHeightBeforePrepend.current = null;
        setHasMoreMessages(false);
        return;
      }
      const reversed = [...olderMessages].reverse();
      setMessages((prev) => [...reversed, ...prev]);
      setOffset((prev) => prev + MESSAGES_PAGE_SIZE);
      setHasMoreMessages(olderMessages.length === MESSAGES_PAGE_SIZE);
    } catch {
      scrollHeightBeforePrepend.current = null;
    } finally {
      setIsLoadingMore(false);
    }
  }, [threadId, offset, hasMoreMessages, isLoadingMore, loadMoreMessages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => {
      if (
        container.scrollTop <= SCROLL_THRESHOLD &&
        hasMoreMessages &&
        !isLoadingMore
      ) {
        void handleLoadMore();
      }
    };
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [hasMoreMessages, isLoadingMore, handleLoadMore]);

  const { stopPolling } = useStreamPolling({
    threadId,
    processingMessageId,
    getStreamState,
    setMessages,
    setIsThinking,
    setStreamingState,
    syncThreadMessages,
  });

  const handleSend = useCallback(
    async (text: string, mode: AssistantModeEnum, retryCount = 0) => {
      setDraft("");
      setIsThinking(true);

      const userMessagePlaceholder = createUserMessage(
        text,
        threadId,
        retryCount,
      );
      const assistantPlaceholder = createAssistantMessage(threadId);

      scrollToBottom();
      setMessages((prev) => [
        ...prev,
        userMessagePlaceholder,
        assistantPlaceholder,
      ]);

      try {
        const res = await sendMessage({
          threadId: threadId ?? "",
          content: text,
          retryCount,
          mode,
        });

        const { threadId: returnedThreadId, userMessage } = res;

        if (!threadId && onThreadIdChange) {
          onThreadIdChange(returnedThreadId);
        }

        setProcessingMessageId(userMessage.id);

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === userMessagePlaceholder.id) {
              return { ...userMessage, threadId: returnedThreadId };
            } else if (msg.id === assistantPlaceholder.id) {
              return { ...msg, threadId: returnedThreadId };
            }
            return msg;
          }),
        );
      } catch (error) {
        await stopPolling();
        clearMessageError(userMessagePlaceholder.id, assistantPlaceholder.id);
        Toast.error({
          title: t("apps.admin.chatAI.messages.errors.toast.sendFailed"),
        });
      }
    },
    [
      threadId,
      sendMessage,
      onThreadIdChange,
      scrollToBottom,
      stopPolling,
      clearMessageError,
      t,
    ],
  );

  const handleRetry = useCallback(
    (messageContent: string, currentRetryCount: number) => {
      void handleSend(messageContent, selectedMode, currentRetryCount + 1);
    },
    [handleSend, selectedMode],
  );

  const handleStop = useCallback(async () => {
    await stopPolling({ sync: true });
    clearStreamingState();

    const lastProcessing = messages
      .slice()
      .reverse()
      .find(
        (message) =>
          message.from === MessageFromEnum.User &&
          message.status === MessageStatusEnum.Processing,
      );

    if (lastProcessing) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === lastProcessing.id &&
          message.status === MessageStatusEnum.Processing
            ? {
                ...message,
                status: MessageStatusEnum.Canceled,
                error: "You canceled this request",
              }
            : message,
        ),
      );
      void cancelMessage(lastProcessing.id)
        .then(async () => {
          if (threadId) {
            return await syncThreadMessages(threadId);
          }
        })
        .catch(() =>
          Toast.error({
            title: t("apps.admin.chatAI.messages.errors.toast.cancelFailed"),
          }),
        );
    }
  }, [
    messages,
    threadId,
    cancelMessage,
    syncThreadMessages,
    stopPolling,
    clearStreamingState,
    t,
  ]);

  return {
    messages,
    draft,
    setDraft,
    selectedMode,
    setSelectedMode,
    isThinking,
    streamingState,
    isLoadingThread,
    threadError,
    handleSend,
    handleRetry,
    handleStop,
    messagesEndRef,
    containerRef,
    isLoadingMore,
  };
};
